import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { cn } from "@/lib/utils";
import { DataTable } from "@/components/common/DataTable";
import { buildTableDefaults } from "@/components/common/data-table-helpers";
import { useTran } from "@/hooks";
import { Badge } from "@/components/ui/badge";
import {
  SURVEY_TYPE_CONFIG,
  RESPONSES_STATUS_CONFIG,
  PRIORITY_CONFIG,
  DECISION_CONFIG,
  FALLBACK_BADGE,
  formatRelativeTime,
  RESPONSES_SITE_OPTIONS,
} from "../utils/survey-manager.helper";

const timeAgo = (iso) => {
  if (!iso) return "—";
  try {
    return formatRelativeTime(new Date(iso));
  } catch {
    return "—";
  }
};
const SurveyResponseTable = ({
  responses = [],
  loading = false,
  sort,
  page,
  pageSize,
  total,
  onSort,
  onPageChange,
  onPageSizeChange,
  onUpdate,
  onView,
}) => {
  const { t } = useTran();

  const { emptyMessage, loadingMessage, getRowProps } = buildTableDefaults({
    t,
    emptyMessageKey: "No responses found.",
  });

  const columns = useMemo(
    () => [
      // ── Respondent ──────────────────────────────────────────────────
      {
        key: "respondent",
        header: t("Respondent"),
        sortable: true,
        sortKey: "respondent.name",
        render: (row) => {
          const isNew = row.status === "new";
          return (
            <div className="flex items-center gap-2">
              {/* Green dot for new */}
              {isNew ? (
                <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
              ) : (
                <span className="w-2 h-2 flex-shrink-0" />
              )}
              <span
                className={cn(
                  "text-sm font-medium whitespace-nowrap",
                  isNew
                    ? "text-gray-900 dark:text-gray-100"
                    : "text-gray-400 dark:text-gray-500",
                )}
              >
                {row.respondent?.name || "—"}
              </span>
            </div>
          );
        },
      },

      // ── Company ─────────────────────────────────────────────────────
      {
        key: "company",
        header: t("Company"),
        render: (row) => {
          const isNew = row.status === "new";
          const company = row.respondent?.company || row.company || "—";
          return (
            <span
              className={cn(
                "text-sm whitespace-nowrap",
                isNew
                  ? "text-gray-700 dark:text-gray-300"
                  : "text-gray-400 dark:text-gray-500",
              )}
            >
              {company}
            </span>
          );
        },
      },

      // ── Survey ──────────────────────────────────────────────────────
      {
        key: "survey",
        header: t("Survey"),
        render: (row) => {
          const isNew = row.status === "new";
          const name = row.survey?.name ?? "—";
          const type = row.survey?.type;
          return (
            <div className="flex items-center gap-2 whitespace-nowrap">
              <span
                className={cn(
                  "text-sm",
                  isNew
                    ? "text-gray-700 dark:text-gray-300"
                    : "text-gray-400 dark:text-gray-500",
                )}
              >
                {name.length > 26 ? name.slice(0, 26) + "…" : name}
              </span>
              {type && (
                <Badge
                  className={SURVEY_TYPE_CONFIG[type]?.style ?? FALLBACK_BADGE}
                >
                  {SURVEY_TYPE_CONFIG[type]?.label ?? type}
                </Badge>
              )}
            </div>
          );
        },
      },

      {
        key: "site",
        header: t("Site"),
        sortable: true,
        sortKey: "site",
        render: (row) => {
          const site = RESPONSES_SITE_OPTIONS.find(
            (opt) => opt.value === row.site,
          );
          return site?.label ?? row.site ?? "—";
        },
      },

      // ── Priority ────────────────────────────────────────────────────
      {
        key: "priorityLabel",
        header: t("Priority"),
        sortable: true,
        sortKey: "priorityLabel",
        render: (row) =>
          row.priorityLabel ? (
            <Badge
              className={
                PRIORITY_CONFIG[row.priorityLabel]?.style ?? FALLBACK_BADGE
              }
            >
              {PRIORITY_CONFIG[row.priorityLabel]?.label ?? row.priorityLabel}
            </Badge>
          ) : (
            <span className="text-gray-300 dark:text-gray-600 text-sm">—</span>
          ),
      },

      // ── Status ──────────────────────────────────────────────────────
      {
        key: "status",
        header: t("Status"),
        sortable: true,
        sortKey: "status",
        render: (row) =>
          row.status ? (
            <Badge
              className={
                RESPONSES_STATUS_CONFIG[row.status]?.style ?? FALLBACK_BADGE
              }
            >
              {RESPONSES_STATUS_CONFIG[row.status]?.label ?? row.status}
            </Badge>
          ) : (
            <span className="text-gray-300 dark:text-gray-600 text-sm">—</span>
          ),
      },

      // ── Submitted ───────────────────────────────────────────────────
      {
        key: "submittedAt",
        header: t("Submitted"),
        sortable: true,
        sortKey: "submittedAt",
        render: (row) => {
          const isNew = row.status === "new";
          return (
            <span
              className={cn(
                "text-sm tabular-nums whitespace-nowrap",
                isNew
                  ? "text-gray-600 dark:text-gray-300"
                  : "text-gray-400 dark:text-gray-500",
              )}
            >
              {timeAgo(row.submittedAt)}
            </span>
          );
        },
      },

      // ── Decision ────────────────────────────────────────────────────
      {
        key: "decisionLabel",
        header: t("Decision"),
        render: (row) =>
          row.decisionLabel ? (
            <Badge
              className={
                DECISION_CONFIG[row.decisionLabel]?.style ?? FALLBACK_BADGE
              }
            >
              {DECISION_CONFIG[row.decisionLabel]?.label ?? row.decisionLabel}
            </Badge>
          ) : (
            <span className="text-gray-300 dark:text-gray-600 text-sm">—</span>
          ),
      },
    ],
    [t, onUpdate, onView],
  );

  const handleSort = (key) => {
    const direction =
      sort?.key === key && sort?.direction === "asc" ? "desc" : "asc";
    onSort?.({ key, direction });
  };

  const handlePageChange = (nextPage) => {
    if (nextPage !== page) onPageChange?.(nextPage);
  };

  const handlePageSizeChange = (nextSize) => {
    if (nextSize !== pageSize) {
      onPageSizeChange?.(nextSize);
      onPageChange?.(1);
    }
  };

  return (
    <DataTable
      columns={columns}
      data={responses}
      loading={loading}
      sort={sort}
      onSort={handleSort}
      emptyMessage={emptyMessage}
      loadingMessage={loadingMessage}
      getRowKey={(row) => row._id}
      getRowProps={(row) => ({
        ...getRowProps(row),
        className: cn(
          getRowProps(row)?.className,
          row.status === "new" && "font-medium",
        ),
        onClick: () => onView?.(row._id),
      })}
      pagination={{
        page,
        pageSize,
        total,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        pageSizeOptions: [10, 20, 50],
        disabled: loading,
        labels: {
          itemsPerPage: t("pagination.itemsPerPage"),
          previous: t("Previous"),
          next: t("Next"),
          pageLabel: ({ page: current, totalPages }) =>
            t("pagination.pageLabel", { page: current, totalPages }),
        },
      }}
    />
  );
};

export default observer(SurveyResponseTable);
